import { useEffect, useMemo, useState, FormEvent } from 'react'
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  User,
  Settings as SettingsIcon,
  Shield,
  CheckCircle2,
  Clock,
  Globe,
  LogOut,
  Loader2,
  Eye,
  Download,
  RefreshCw,
  Filter,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import Profile from './settings/Profile'
import { useAuth, usePasswordManagement, useEmailVerification } from '@/hooks/useAuth'
import { useAuditLog } from '@/utils/auditLogger'

const settingsSections = [
  {
    id: 'overview',
    title: 'Account Overview',
    description: 'Review verification, approval, and environment details',
    icon: User,
    href: '/settings',
  },
  {
    id: 'profile',
    title: 'Profile',
    description: 'Manage your personal and company information',
    icon: SettingsIcon,
    href: '/settings/profile',
  },
  {
    id: 'security',
    title: 'Security',
    description: 'Update your password and email verification preferences',
    icon: Shield,
    href: '/settings/security',
  },
  {
    id: 'audit',
    title: 'Audit Logs',
    description: 'View system audit logs and activity monitoring',
    icon: Eye,
    href: '/settings/audit',
  },
]

export default function Settings() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const location = useLocation()
  const { user, checkAuth } = useAuth()

  useEffect(() => {
    if (!user) {
      checkAuth()
    }
  }, [user, checkAuth])

  const pathWithoutQuery = location.pathname.split('?')[0]
  const normalizedPath =
    pathWithoutQuery.endsWith('/') && pathWithoutQuery !== '/settings'
      ? pathWithoutQuery.slice(0, -1)
      : pathWithoutQuery

  const activeSection = useMemo(() => {
    const match = settingsSections.find(section => section.href === normalizedPath)
    return match ?? settingsSections[0]
  }, [normalizedPath])

  return (
    <div className="min-h-screen" style={pageStyles.container}>
      <div className="px-6 py-10 mx-auto max-w-7xl" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
        <div className="space-y-8">
          <main className="w-full">
            <div className="flex items-center gap-3 mb-6">
              <div>
                <p className="text-sm" style={pageStyles.textSecondary}>{activeSection.description}</p>
              </div>
            </div>

            <Routes>
              <Route index element={<AccountOverview />} />
              <Route path="profile" element={<Profile />} />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="audit" element={<AuditLogSettings />} />
              <Route path="*" element={<Navigate to="/settings" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  )
}

function AccountOverview() {
  const { user } = useAuth()
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  if (!user) {
    return (
      <div className="transition-all duration-300 border card hover:shadow-xl" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
        <div className="card-content">
          <p className="text-sm" style={pageStyles.textSecondary}>Loading account insights from the auth service...</p>
        </div>
      </div>
    )
  }

  const profileFields: Array<keyof typeof user> = [
    'first_name',
    'last_name',
    'email',
    'company_name',
    'phone_number',
    'address',
    'country',
  ]

  const completedFields = profileFields.filter(field => {
    const value = user[field]
    if (value === null || value === undefined) return false
    if (typeof value === 'string') {
      return value.trim().length > 0
    }
    return true
  })

  const completionScore = Math.round((completedFields.length / profileFields.length) * 100)

  const statusItems = [
    {
      label: 'Email verification',
      value: user.is_verified ? 'Verified' : 'Pending verification',
      tone: user.is_verified ? 'text-emerald-600' : 'text-amber-600',
      icon: user.is_verified ? CheckCircle2 : Clock,
    },
    {
      label: 'Account approval',
      value: user.is_account_approved ? 'Approved' : 'Awaiting review',
      tone: user.is_account_approved ? 'text-emerald-600' : 'text-amber-600',
      icon: user.is_account_approved ? CheckCircle2 : Clock,
    },
    {
      label: 'Environment',
      value: user.environment === 'production' ? 'Production' : 'Sandbox',
      tone: user.environment === 'production' ? 'text-sky-600' : 'text-indigo-600',
      icon: Globe,
    },
  ]

  return (
    <div className="space-y-6">
      <div className="transition-all duration-300 border card hover:shadow-xl" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
        <div className="card-header">
          <h3 className="text-2xl" style={pageStyles.text}>Account status</h3>
          <p className="text-gray-300 card-description" style={pageStyles.textSecondary}>
            Live summary of your authentication footprint.
          </p>
        </div>
        <div className="grid gap-4" style={{ ...cardStyles, gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {statusItems.map(item => {
            const Icon = item.icon
            return (
              <div
                key={item.label}
                className="px-4 py-5 border border-gray-600 shadow-sm rounded-2xl" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-10 h-10" style={{ backgroundColor: statusColors.success.backgroundColor, borderRadius: '10px' }}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="text-xs font-medium tracking-wide" style={pageStyles.textSecondary}>
                      {item.label}
                    </p>
                    <p className={`mt-1 text-sm font-semibold ${item.tone}`} style={pageStyles.text}>{item.value}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="w-full">
        <div className="transition-all duration-300 border card hover:shadow-xl" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
          <div className="card-header">
            <h3 className="text-xl" style={pageStyles.text}>Profile completeness</h3>
            <p className="text-gray-300 card-description" style={pageStyles.textSecondary}>
              Complete your profile so the auth service can provision access swiftly.
            </p>
          </div>
          <div className="space-y-4" style={{ ...cardStyles }}>
            <div>
              <div className="flex items-center justify-between text-sm font-semibold" style={pageStyles.textSecondary}>
                <span>{completionScore}% complete</span>
                <span className="text-xs font-medium" style={pageStyles.textSecondary}>
                  {completedFields.length} / {profileFields.length} fields
                </span>
              </div>
              <div className="mt-3 h-2.5 w-full rounded-full" style={{ backgroundColor: statusColors.success.backgroundColor }}>
                <div
                  className="h-2.5 rounded-full" style={{ backgroundColor: statusColors.success.backgroundColor, width: `${Math.max(completionScore, 6)}%` }}
                />
              </div>
            </div>
            <p className="text-sm" style={pageStyles.textSecondary}>
              Keep your contact and company information current to prevent authentication delays.
              Missing details can slow down approvals and security reviews.
            </p>
            <div>
              <Button size="sm" className="text-white bg-orange-500 hover:bg-orange-600" asChild style={{ backgroundColor: statusColors.success.backgroundColor }}>
                <NavLink to="/settings/profile">Update profile details</NavLink>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function SecuritySettings() {
  const { user, logout, forgotPassword } = useAuth()
  const { changePassword } = usePasswordManagement()
  const { resendVerificationEmail } = useEmailVerification()
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [isSendingForgotPassword, setIsSendingForgotPassword] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)

  const handleChangePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!currentPassword || !newPassword) return
    setIsChangingPassword(true)
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword })
      setCurrentPassword('')
      setNewPassword('')
    } finally {
      setIsChangingPassword(false)
    }
  }

  const handleResendVerification = async () => {
    setIsResending(true)
    try {
      await resendVerificationEmail()
    } finally {
      setIsResending(false)
    }
  }

  const handleForgotPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!forgotPasswordEmail) return
    setIsSendingForgotPassword(true)
    try {
      await forgotPassword({ email: forgotPasswordEmail })
      setForgotPasswordEmail('')
    } finally {
      setIsSendingForgotPassword(false)
    }
  }

  const handleLogout = async () => {
    setIsSigningOut(true)
    try {
      await logout()
    } finally {
      setIsSigningOut(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
        <div className="card-header">
          <h3 className="text-2xl card-title" style={pageStyles.text}>Credential management</h3>
          <p className="card-description" style={pageStyles.textSecondary}>
            Update your password through the auth service.
          </p>
        </div>
        <div className="card-content">
          <form className="space-y-5" onSubmit={handleChangePassword}>
            <div>
              <label
                className="block mb-2 text-sm font-medium"
                style={pageStyles.textSecondary}
                htmlFor="current-password"
              >
                Current password
              </label>
              <Input
                id="current-password"
                type="password"
                placeholder="Enter your current password"
                value={currentPassword}
                onChange={event => setCurrentPassword(event.target.value)}
                required
              />
            </div>
            <div>
              <label
                className="block mb-2 text-sm font-medium text-gray-300"
                htmlFor="new-password"
              >
                New password
              </label>
              <Input
                id="new-password"
                type="password"
                placeholder="Create a strong password"
                value={newPassword}
                onChange={event => setNewPassword(event.target.value)}
                minLength={8}
                required
              />
              <p className="mt-2 text-xs" style={pageStyles.textSecondary}>
                Minimum 8 characters, with a mix of numbers and symbols.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating password...
                  </>
                ) : (
                  'Update password'
                )}
              </Button>
              <p className="text-xs" style={pageStyles.textSecondary}>
                Password changes invalidate sessions issued by the auth service.
              </p>
            </div>
          </form>
        </div>
      </div>

      <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
        <div className="card-header">
          <h3 className="text-2xl card-title" style={pageStyles.text}>Forgot Password</h3>
          <p className="card-description" style={pageStyles.textSecondary}>
            Reset your password if you've forgotten it.
          </p>
        </div>
        <div className="card-content">
          <form className="space-y-5" onSubmit={handleForgotPassword}>
            <div>
              <label
                className="block mb-2 text-sm font-medium"
                style={pageStyles.textSecondary}
                htmlFor="forgot-password-email"
              >
                Email address
              </label>
              <Input
                id="forgot-password-email"
                type="email"
                placeholder="Enter your email address"
                value={forgotPasswordEmail}
                onChange={event => setForgotPasswordEmail(event.target.value)}
                required
              />
              <p className="mt-2 text-xs" style={pageStyles.textSecondary}>
                We'll send you a password reset link to this email address.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={isSendingForgotPassword} variant="outline">
                {isSendingForgotPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending reset link...
                  </>
                ) : (
                  'Send reset link'
                )}
              </Button>
              <p className="text-xs" style={pageStyles.textSecondary}>
                Check your email for the password reset instructions.
              </p>
            </div>
          </form>
        </div>
      </div>

      <div className="space-y-6">
        <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
          <div className="card-header">
            <h3 className="text-xl card-title" style={pageStyles.text}>Email verification</h3>
            <p className="card-description" style={pageStyles.textSecondary}>
              Ensure your login email is ready for secure operations.
            </p>
          </div>
          <div className="space-y-4 card-content">
            <div className="flex items-center justify-between px-4 py-3 border shadow-sm rounded-xl" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
              <div>
                <p className="text-sm font-semibold" style={pageStyles.text}>Status</p>
                <p className="text-xs" style={pageStyles.textSecondary}>
                  {user?.is_verified
                    ? 'Email verified with the auth service'
                    : 'Verification is still pending'}
                </p>
              </div>
              <span
                className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: user?.is_verified ? statusColors.success.color : statusColors.warning.color }}
              >
                {user?.is_verified ? 'Verified' : 'Pending'}
              </span>
            </div>
            {!user?.is_verified && (
              <Button onClick={handleResendVerification} disabled={isResending} variant="outline">
                {isResending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending email...
                  </>
                ) : (
                  'Resend verification email'
                )}
              </Button>
            )}
          </div>
        </div>

        <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
          <div className="card-header">
            <h3 className="text-xl card-title" style={pageStyles.text}>Session controls</h3>
            <p className="card-description" style={pageStyles.textSecondary}>
              Sign out to terminate the current authenticated session.
            </p>
          </div>
          <div className="space-y-4 card-content">
            <div className="px-4 py-3 border shadow-sm rounded-xl" style={{ ...cardStyles, borderColor: statusColors.success.borderColor }}>
              <p className="text-sm font-semibold" style={pageStyles.text}>Current session</p>
              <p className="mt-1 text-xs" style={pageStyles.textSecondary}>
                Active since{' '}
                {user?.last_login ? new Date(user.last_login).toLocaleString() : 'first login'}. Use
                the sign-out action to revoke tokens issued by the auth service.
              </p>
            </div>
            <Button
              onClick={handleLogout}
              variant="destructive"
              disabled={isSigningOut}
              className="flex items-center"
            >
              {isSigningOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing out...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4 mr-2" /> Sign out of this session
                </>
              )}
            </Button>
            <p className="text-xs" style={pageStyles.textSecondary}>
              Sign out to clear the stored tokens on this device.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function AuditLogSettings() {
  const { user } = useAuth()
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const { getLogs, getAuditStats, exportLogs } = useAuditLog()
  const [logs, setLogs] = useState<any[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [showStats, setShowStats] = useState(false)

  const loadLogs = async () => {
    setLoading(true)
    try {
      const recentLogs = getLogs().slice(0, 50) // Get last 50 logs
      console.log('🔍 Debug: Retrieved logs:', recentLogs.length, recentLogs)
      setLogs(recentLogs)

      const auditStats = getAuditStats()
      console.log('🔍 Debug: Audit stats:', auditStats)
      setStats(auditStats)
    } catch (error) {
      console.error('Error loading audit logs:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLogs()
  }, [])

  // Function to create sample audit logs for testing
  const createSampleLogs = () => {
    if (!user) return

    const { logPermissionCheck, logUserAction, logDataAccess, logDataModification } = useAuditLog()

    // Create some sample logs
    logPermissionCheck(user, 'view_audit_logs', 'audit_logs', undefined, true, 'User accessed audit logs')
    logUserAction(user, 'test_action', 'settings', undefined, { testData: 'sample' })
    logDataAccess(user, 'user_profile', user.id, true, 'Accessed user profile')
    logDataModification(user, 'update_settings', 'settings', undefined, true, 'Updated user settings')

    // Create a denied permission log
    logPermissionCheck(user, 'admin_only_action', 'admin_panel', undefined, false, 'Insufficient permissions')

    // Refresh logs after creating samples
    setTimeout(() => {
      loadLogs()
    }, 100)
  }

  const formatTimestamp = (timestamp: Date) => {
    return new Date(timestamp).toLocaleString()
  }

  const getActionBadgeColor = (action: string) => {
    switch (action) {
      case 'permission_check':
        return 'bg-blue-100 text-blue-800'
      case 'data_access':
        return 'bg-green-100 text-green-800'
      case 'data_modification':
        return 'bg-orange-100 text-orange-800'
      case 'user_action':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getGrantedBadgeColor = (granted: boolean) => {
    return granted
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800'
  }

  if (!user || (user.role !== 'admin' && user.role !== 'auditor')) {
    return (
      <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
        <div className="card-header">
          <h3 className="text-2xl card-title flex items-center gap-2" style={pageStyles.text}>
            <Eye className="h-5 w-5" style={{ color: branding.textSecondaryColor }} />
            Audit Log Viewer
          </h3>
        </div>
        <div className="card-content">
          <p style={pageStyles.textSecondary}>
            You don't have permission to view audit logs. Only admins and auditors can access this feature.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl card-title flex items-center gap-2" style={pageStyles.text}>
              <Eye className="h-5 w-5" style={{ color: branding.textSecondaryColor }} />
              Audit Log Viewer
            </h3>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowStats(!showStats)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showStats ? 'Hide Stats' : 'Show Stats'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadLogs}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const exportData = exportLogs()
                  const blob = new Blob([exportData], { type: 'application/json' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`
                  a.click()
                  URL.revokeObjectURL(url)
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={createSampleLogs}
                style={{ backgroundColor: branding.secondaryColor, color: branding.textColor }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Create Sample Logs
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {showStats && stats && (
        <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
          <div className="card-header">
            <h3 className="text-2xl card-title" style={pageStyles.text}>Audit Statistics</h3>
          </div>
          <div className="card-content">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: branding.secondaryColor }}>{stats.totalLogs}</div>
                <div className="text-sm" style={pageStyles.textSecondary}>Total Logs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: statusColors.success.color }}>{stats.grantedLogs}</div>
                <div className="text-sm" style={pageStyles.textSecondary}>Granted</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: statusColors.error.color }}>{stats.deniedLogs}</div>
                <div className="text-sm" style={pageStyles.textSecondary}>Denied</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: branding.primaryColor }}>{stats.uniqueUsers}</div>
                <div className="text-sm" style={pageStyles.textSecondary}>Unique Users</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={cardStyles}>
        <div className="card-header">
          <h3 className="text-2xl card-title" style={pageStyles.text}>Recent Audit Logs</h3>
        </div>
        <div className="card-content">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.textSecondaryColor }} />
              <p style={pageStyles.textSecondary}>Loading audit logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <Eye className="h-8 w-8 mx-auto mb-4" style={{ color: branding.textSecondaryColor }} />
              <p style={pageStyles.textSecondary}>No audit logs found</p>
              <p className="text-sm" style={{ color: `${branding.textSecondaryColor}80` }}>Perform some actions to see audit logs here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="border rounded-lg p-4 transition-colors"
                  style={{
                    borderColor: statusColors.success.borderColor,
                    backgroundColor: `${branding.surfaceColor}50`
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={getActionBadgeColor(log.action)}>
                        {log.action.replace('_', ' ')}
                      </Badge>
                      <Badge className={getGrantedBadgeColor(log.granted)}>
                        {log.granted ? 'Granted' : 'Denied'}
                      </Badge>
                    </div>
                    <span className="text-sm" style={pageStyles.textSecondary}>
                      {formatTimestamp(log.timestamp)}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium" style={pageStyles.text}>User:</span>
                      <span className="text-sm" style={pageStyles.textSecondary}>{log.userEmail}</span>
                      <Badge variant="outline">{log.userRole}</Badge>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-medium" style={pageStyles.text}>Resource:</span>
                      <span className="text-sm" style={pageStyles.textSecondary}>{log.resource}</span>
                      {log.resourceId && (
                        <span className="text-sm" style={{ color: `${branding.textSecondaryColor}80` }}>(ID: {log.resourceId})</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-medium" style={pageStyles.text}>Permission:</span>
                      <span className="text-sm" style={pageStyles.textSecondary}>{log.permission}</span>
                    </div>

                    {log.reason && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium" style={pageStyles.text}>Reason:</span>
                        <span className="text-sm" style={pageStyles.textSecondary}>{log.reason}</span>
                      </div>
                    )}

                    {log.metadata && Object.keys(log.metadata).length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-sm font-medium" style={pageStyles.textSecondary}>
                          Metadata
                        </summary>
                        <pre className="mt-2 text-xs p-2 rounded overflow-x-auto" style={{ backgroundColor: `${branding.backgroundColor}80`, color: branding.textColor }}>
                          {JSON.stringify(log.metadata, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
